/**
 * Classify a media row by its MIME type and URL into one of four kinds.
 *
 * Meteor-migrated rows often arrive with `media_type='photo'` but a
 * `mime_type` and URL that say otherwise (PDFs, MP4s, YouTube embeds,
 * .docx files). The page treats `media_type` as a hint and routes
 * rendering off this function's output instead.
 *
 * Used by:
 * - apps/web/app/(adaptive)/dancers/[slug]/page.tsx (public dancer profile)
 * - unit tests in apps/web/app/__tests__/unit/artists/classify-media-kind.test.ts
 *
 * Decision order:
 *   1. mime starts with `image/` → image
 *   2. mime starts with `video/` → video
 *   3. mime in DOCUMENT_MIMES → document
 *   4. URL ends with image extension → image
 *   5. URL ends with video extension → video
 *   6. URL ends with document extension → document
 *   7. URL host is a known video host (YouTube/Vimeo) → video
 *   8. Fallback → unknown (caller should drop the row)
 */
export type MediaKind = 'image' | 'video' | 'document' | 'unknown';

const IMAGE_EXTENSIONS =
  /\.(jpe?g|png|webp|gif|avif|heic|heif|bmp|tiff?)(\?|#|$)/i;
const VIDEO_EXTENSIONS = /\.(mp4|mov|webm|m4v|ogv)(\?|#|$)/i;
// Match the host segment after `://`, after a `.` (subdomain boundary), or
// at start-of-string. Without `\/\/` the regex would only match
// `www.youtube.com` / `m.youtube.com` and miss bare `https://youtu.be/abc`
// — silently dropping ~2.3k YouTube reels from the Meteor migration.
const VIDEO_HOSTS = /(?:^|\.|\/\/)(youtube\.com|youtu\.be|vimeo\.com)(?:\/|$)/i;
const DOCUMENT_EXTENSIONS = /\.(pdf|docx?|pages|odt)(\?|#|$)/i;
const DOCUMENT_MIMES = new Set([
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/x-iwork-pages-sffpages',
  'application/vnd.oasis.opendocument.text',
]);

export function classifyMediaKind(mime: string | null, url: string): MediaKind {
  const m = (mime ?? '').toLowerCase();
  if (m.startsWith('image/')) return 'image';
  if (m.startsWith('video/')) return 'video';
  if (DOCUMENT_MIMES.has(m)) return 'document';
  if (IMAGE_EXTENSIONS.test(url)) return 'image';
  if (VIDEO_EXTENSIONS.test(url)) return 'video';
  if (DOCUMENT_EXTENSIONS.test(url)) return 'document';
  // Embed-style URLs (YouTube/Vimeo) carry no extension and arrive as
  // application/octet-stream from the Meteor migration. Host-based
  // detection catches the ~2.3k YouTube reels in the dataset.
  if (VIDEO_HOSTS.test(url)) return 'video';
  return 'unknown';
}
